import { IPQuestion } from "modules/organization/models/interface";
import {
  EquationParser,
  ILogicalExpression,
  RQL_OPERATORS,
} from "../../utils/equationParser";
import {
  buildAnswerNotExistsResponseVariableMap,
  buildOpenEndedQuestionVariableSet,
} from "../../utils/equationValidation";

export interface IEquationErrorRange {
  from: number;
  to: number;
  message: string;
}

const OPERATOR_PATTERN = new RegExp(`^(${RQL_OPERATORS.join("|")})$`);
const COMPARE_OPERATORS = ["=", "!=", "<", ">", ">=", "<="];
const COMPARE_TYPES = ["NUMBER", "DATE"];

// This is a SEPARATE, independent walk of the same parsed equation tree that
// equationValidation.ts's collectEquationContentErrors already walks to produce the plain-text
// error message list — deliberately not sharing code with it, so this purely-additive feature
// can never change what that function returns or how Save/the error list behave. This walk's
// only job is: for each single-value content error (unknown variable, unknown rank, self/higher
// rank IMPORT, invalid COMPARE operator/type), locate that value's character range in the RAW
// equation text so it can be underlined. Whole-call structural errors (wrong argument count) are
// skipped here — there's no single token to point at for those, and they're already visible via
// the existing red border + message list.
const findOccurrence = (
  equationText: string,
  value: string,
  searchFrom: number,
): { from: number; to: number } | null => {
  const idx = equationText.indexOf(value, searchFrom);
  if (idx === -1) {
    return null;
  }
  return { from: idx, to: idx + value.length };
};

const answerNotExistsError = (
  variable: string,
  answerNotExistsResponseVars: Map<string, string>,
): string => {
  const questionVar = answerNotExistsResponseVars.get(variable);
  return `${variable} can't be used here — use QHASNOTANSWER(${questionVar}) instead.`;
};

const openEndedQandQorError = (operatorName: string, questionVar: string): string =>
  `${operatorName}(${questionVar}) can't be used here — this question doesn't support ${operatorName}. Use QHASANSWER or QHASNOTANSWER instead.`;

const walk = (
  exprPair: ILogicalExpression,
  equationText: string,
  cursor: { pos: number },
  knownQuestionVars: Set<string>,
  knownResponseVars: Set<string>,
  answerNotExistsResponseVars: Map<string, string>,
  openEndedQuestionVars: Set<string>,
  rankDisplayOrderByName: Map<string, number | null | undefined>,
  selfDisplayOrder: number | null | undefined,
  ranges: IEquationErrorRange[],
) => {
  const operatorName =
    typeof exprPair[0] === "string" ? exprPair[0].toUpperCase() : "";
  const argsArray = exprPair[1];
  if (!Array.isArray(argsArray)) {
    return;
  }

  const allowsNestedExpressions = operatorName === "AND" || operatorName === "OR";
  const plainArgs: string[] = [];
  // Locate each plain argument's occurrence AS we walk (not in a separate pass after the loop
  // finishes) — the shared cursor must advance in true left-to-right TEXT order. A nested
  // AND/OR's own arguments physically appear LATER in the raw text than this level's remaining
  // plain arguments, but recursing into it happens mid-loop; batching all of this level's
  // occurrence lookups into a single pass AFTER the loop (as this used to) meant any plain
  // argument that appeared BEFORE a nested call in argsArray, but whose lookup only ran after
  // that nested call had already advanced the cursor past it, would search from the wrong
  // position and silently fail to match (findOccurrence returning null) — exactly why only the
  // deepest/last-processed leaf arguments were getting flagged.
  const occurrences: ({ from: number; to: number } | null)[] = [];
  for (let i = 0; i < argsArray.length; i++) {
    const item = argsArray[i];
    const next = argsArray[i + 1];
    if (
      allowsNestedExpressions &&
      typeof item === "string" &&
      OPERATOR_PATTERN.test(item.toUpperCase()) &&
      Array.isArray(next)
    ) {
      walk(
        [item, next],
        equationText,
        cursor,
        knownQuestionVars,
        knownResponseVars,
        answerNotExistsResponseVars,
        openEndedQuestionVars,
        rankDisplayOrderByName,
        selfDisplayOrder,
        ranges,
      );
      i++;
      continue;
    }
    if (typeof item === "string") {
      plainArgs.push(item);
      const occ = findOccurrence(equationText, item, cursor.pos);
      if (occ) {
        cursor.pos = occ.to;
      }
      occurrences.push(occ);
    }
  }

  const flag = (index: number, message: string) => {
    const occ = occurrences[index];
    if (occ) {
      ranges.push({ ...occ, message });
    }
  };

  if (operatorName === "AND" || operatorName === "OR") {
    plainArgs.forEach((variable, index) => {
      if (!knownResponseVars.has(variable)) {
        flag(index, `Response variable not found: ${variable}`);
      } else if (answerNotExistsResponseVars.has(variable)) {
        flag(index, answerNotExistsError(variable, answerNotExistsResponseVars));
      }
    });
  } else if (operatorName === "QAND" || operatorName === "QOR") {
    if (plainArgs.length === 1) {
      if (!knownQuestionVars.has(plainArgs[0])) {
        flag(0, `Question variable not found: ${plainArgs[0]}`);
      } else if (openEndedQuestionVars.has(plainArgs[0])) {
        flag(0, openEndedQandQorError(operatorName, plainArgs[0]));
      }
    }
  } else if (operatorName === "QHASANSWER" || operatorName === "QHASNOTANSWER") {
    if (plainArgs.length === 1 && !knownQuestionVars.has(plainArgs[0])) {
      flag(0, `Question variable not found: ${plainArgs[0]}`);
    }
  } else if (operatorName === "EQUAL" || operatorName === "NOTEQUAL") {
    if (plainArgs.length === 2) {
      if (!knownResponseVars.has(plainArgs[0])) {
        flag(0, `Response variable not found: ${plainArgs[0]}`);
      } else if (answerNotExistsResponseVars.has(plainArgs[0])) {
        flag(0, answerNotExistsError(plainArgs[0], answerNotExistsResponseVars));
      }
    }
  } else if (operatorName === "COMPARE") {
    if (plainArgs.length === 3 || plainArgs.length === 4) {
      if (!knownResponseVars.has(plainArgs[0])) {
        flag(0, `Response variable not found: ${plainArgs[0]}`);
      } else if (answerNotExistsResponseVars.has(plainArgs[0])) {
        flag(0, answerNotExistsError(plainArgs[0], answerNotExistsResponseVars));
      }
      if (!COMPARE_OPERATORS.includes(plainArgs[1])) {
        flag(
          1,
          `COMPARE's second parameter must be one of ${COMPARE_OPERATORS.join(", ")} — found '${plainArgs[1]}'.`,
        );
      }
      if (
        plainArgs.length === 4 &&
        !COMPARE_TYPES.includes(plainArgs[3].toUpperCase())
      ) {
        flag(
          3,
          `COMPARE's fourth parameter must be one of ${COMPARE_TYPES.join(", ")} — found '${plainArgs[3]}'.`,
        );
      }
    }
  } else if (operatorName === "IMPORT") {
    if (plainArgs.length === 1) {
      const rankName = plainArgs[0];
      if (!rankDisplayOrderByName.has(rankName)) {
        flag(0, `Rank not found: ${rankName}`);
      } else if (selfDisplayOrder != null) {
        const targetDisplayOrder = rankDisplayOrderByName.get(rankName);
        if (targetDisplayOrder != null) {
          if (targetDisplayOrder === selfDisplayOrder) {
            flag(0, `${rankName} is this same rank — a rank cannot IMPORT itself.`);
          } else if (targetDisplayOrder < selfDisplayOrder) {
            flag(
              0,
              `${rankName} is a higher rank and cannot be imported into a lower rank.`,
            );
          }
        }
      }
    }
  }
};

// Returns the character ranges to underline for the equation's content errors. Syntax errors
// (mismatched parens, malformed structure) are intentionally not covered — EquationParser's
// tokenizer doesn't track character offsets, and those already get a strong signal via the
// editor's existing red-border (hasError) styling.
export const findEquationErrorRanges = (
  equationText: string,
  questions: IPQuestion[],
  rankDisplayOrderByName: Map<string, number | null | undefined>,
  selfDisplayOrder: number | null | undefined,
): IEquationErrorRange[] => {
  if (!equationText.trim()) {
    return [];
  }
  const parsed = new EquationParser(equationText);
  if (parsed.getHasError()) {
    return [];
  }

  const knownQuestionVars = new Set(questions.map((each) => each.question_variable));
  const knownResponseVars = new Set(
    questions.flatMap((each) => each.responses.map((res) => res.response_variable)),
  );
  const answerNotExistsResponseVars = buildAnswerNotExistsResponseVariableMap(questions);
  const openEndedQuestionVars = buildOpenEndedQuestionVariableSet(questions);

  const ranges: IEquationErrorRange[] = [];
  const exprPair = parsed.getEquationStack();
  if (Array.isArray(exprPair) && exprPair.length === 2) {
    walk(
      exprPair,
      equationText,
      { pos: 0 },
      knownQuestionVars,
      knownResponseVars,
      answerNotExistsResponseVars,
      openEndedQuestionVars,
      rankDisplayOrderByName,
      selfDisplayOrder,
      ranges,
    );
  }
  return ranges;
};
